import { ApolloLink, Operation, FetchResult, Observable, Observer } from '@apollo/client';
import { ConnectionParams, SubscriptionClient, ClientOptions } from 'subscriptions-transport-ws';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';

const LOG = LoggerFactory.create('WebSocketLink');

export interface WebSocketLinkOptions {
  uri: string;
  options: ClientOptions;
}

interface OperationQueueEntry {
  operation: Operation;
  observer: Observer<FetchResult>;
}

/**
 * WebSocketLink that queues operations until connectionParams can complete.
 * This prevents sending operations before we've had time to authenticate with a one time token.
 *
 * Inspired by apollo-link-queue https://github.com/helfer/apollo-link-queue/blob/master/src/QueueLink.ts
 */
export class WebSocketLink extends ApolloLink {
  private subscriptionClient: SubscriptionClient;

  private hasConnectionParams = false;

  private queuedOperations: OperationQueueEntry[] = [];

  constructor({ uri, options }: WebSocketLinkOptions) {
    super();
    const { connectionParams } = options;
    this.subscriptionClient = new SubscriptionClient(uri, {
      ...options,
      connectionParams: async (): Promise<ConnectionParams> => {
        this.hasConnectionParams = false;
        let params: ConnectionParams;
        if (typeof connectionParams === 'function') {
          params = await connectionParams();
        } else {
          params = connectionParams;
        }

        // Delay running queued operations so we can return the params
        setTimeout(() => {
          this.hasConnectionParams = true;
          LOG.debug('connectionParams fetched, running queued operations...', this.queuedOperations);
          this.runQueuedOperations();
        }, 10);

        return params;
      }
    });
  }

  private runQueuedOperations(): void {
    this.queuedOperations.forEach(({ operation, observer }) => {
      LOG.debug('Running queued operation', operation);
      this.subscriptionClient.request(operation).subscribe(observer);
    });
    this.queuedOperations = [];
  }

  public request(operation: Operation): Observable<FetchResult> | null {
    if (this.hasConnectionParams) {
      return this.subscriptionClient.request(operation) as Observable<FetchResult>;
    }

    return new Observable<FetchResult>((observer: Observer<FetchResult>) => {
      LOG.debug('connectionParams not yet resolved, queuing operation', operation);
      const operationEntry = { operation, observer };
      this.queuedOperations.push(operationEntry);
    });
  }
}
